// src/Router.jsx
import React, { useEffect } from "react";
import { BrowserRouter, MemoryRouter, Route, Routes, useLocation } from "react-router-dom";
import AdsModalPage from "./pages/AdsModalPage";
import BackController from "./bridges/BackController";
import { UIStateProvider } from "./bridges/uiStateContext";
import ProtectedRoute from "./pages/CMS/Admin/Login/ProtectRoute";
import { AuthProvider } from "./pages/CMS/Admin/Login/AuthContext";
import AdminLayout from "./pages/CMS/CommonComponents/AdminLayout";
import RecoveryOverlay from "./features/RecoveryOverlay";
import ConciergeUserProtectedRoute from "./pages/CMS/ConciergeUser/Auth/ConciergeUserProtectedRoute";
import { ConciergeUserAuthProvider } from "./pages/CMS/ConciergeUser/Auth/ConciergeUserAuthContext";
import ConciergeUserLayout from "./pages/CMS/ConciergeUser/Common/ConciergeUserLayout";

// WebView 안인지 판단하여 WebView면 URL을 안 바꾸는 MemoryRouter 사용 -> 스플래시 방지
const isWebView = typeof window !== "undefined" && !!window.ReactNativeWebView;
const initialEntry =
  typeof window !== "undefined"
    ? window.location.pathname + window.location.search + window.location.hash
    : "/";

function HashScrollHandler() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const hash = location.hash || window.location.hash;
    if (!hash) {
      return undefined;
    }

    const targetId = decodeURIComponent(hash.replace(/^#/, ""));
    let cancelled = false;
    let attempts = 0;
    let timerId = null;

    const scrollToHashTarget = () => {
      if (cancelled) {
        return;
      }

      const target = document.getElementById(targetId);
      if (target) {
        target.scrollIntoView({ behavior: "auto", block: "start" });
        return;
      }

      attempts += 1;
      if (attempts < 10) {
        timerId = window.setTimeout(scrollToHashTarget, 150);
      }
    };

    timerId = window.setTimeout(scrollToHashTarget, 0);

    return () => {
      cancelled = true;
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [location.pathname, location.hash]);

  return null;
}

function AppRoutes() {
  const location = useLocation();
  const state = location && typeof location.state === "object" ? location.state : null;
  const background = state && state.background ? state.background : null;

  // // ios 바디 스크롤 방지
  // useEffect(() => {
  //   const platform = localStorage.getItem("platform");

  //   if (platform === "ios") {
  //     const prevOverflow = document.body.style.overflow;
  //     document.body.style.overflow = "hidden";

  //     return () => {
  //       document.body.style.overflow = prevOverflow;
  //     };
  //   }
  // }, []);

  return (
    <>
      <HashScrollHandler />
      <Routes location={background || location}>
        {/* 공개 라우트 */}
        <Route path="/" element={<AdsModalPage type="home" />} />
        <Route path="/concierge" element={<AdsModalPage type="wizmarketConcierge" />} />
        <Route path="/account/delete" element={<AdsModalPage type="accountDeletionRequest" />} />
        <Route path="/concierge/manage/login" element={<AdsModalPage type="conciergeUserLogin" />} />
        <Route path="/concierge/manage/find-pin" element={<AdsModalPage type="conciergeUserFindPin" />} />
        <Route path="/internal/template-capture" element={<AdsModalPage type="internalTemplateCapture" />} />
        <Route path="/help" element={<AdsModalPage type="help" />} />
        <Route path="/help/faq" element={<AdsModalPage type="helpFaq" />} />
        <Route path="/help/inquiry" element={<AdsModalPage type="helpInquiry" />} />
        <Route path="/help/notice" element={<AdsModalPage type="helpNotice" />} />
        <Route path="/help/notice/:notice_no" element={<AdsModalPage type="helpNoticeDetail" />} />
        <Route path="/inapp" element={<AdsModalPage type="inapp" />} />

        {/* 관리자 로그인 (공개) */}
        <Route path="/admin/login" element={<AdsModalPage type="adminLogin" />} />

        {/* CMS 공통 보호 영역 */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AdminLayout />}>
            <Route path="/cms" element={<AdsModalPage type="cms" />} />
            <Route path="/cms/statistics" element={<AdsModalPage type="cmsStats" />} />
            <Route path="/cms/business/list" element={<AdsModalPage type="businessList" />} />
            <Route path="/cms/notice" element={<AdsModalPage type="cmsNotice" />} />
            <Route path="/cms/notice/create" element={<AdsModalPage type="CmsNoticeCreate" />} />
            <Route path="/cms/notice/edit/:notice_no" element={<AdsModalPage type="CmsNoticeCreate" />} />
            <Route path="/cms/notice/:notice_no" element={<AdsModalPage type="CmsNoticeDetail" />} />
            <Route path="/cms/faq" element={<AdsModalPage type="cmsFaq" />} />
            <Route path="/cms/faq/create" element={<AdsModalPage type="cmsFaqCreate" />} />
            <Route path="/cms/inquiry" element={<AdsModalPage type="cmsInquiry" />} />
            <Route path="/cms/inquiry/:id" element={<AdsModalPage type="cmsInquiryDetail" />} />


            <Route path="/cms/concierge" element={<AdsModalPage type="conciergeList" />} />
            <Route path="/concierge/history" element={<AdsModalPage type="conciergeHistory" />} />
            <Route path="/concierge/dashboard" element={<AdsModalPage type="conciergeDashboard" />} />
            <Route element={<ProtectedRoute roles={['SUPER', 'MANAGER']} />}>
              <Route path="/cms/caption-lab" element={<AdsModalPage type="captionLab" />} />
              <Route path="/cms/youtube-lab" element={<AdsModalPage type="youtubeLab" />} />
              <Route path="/cms/threads-lab" element={<AdsModalPage type="threadsLab" />} />
            </Route>


            <Route path="/cms/auth/name" element={<AdsModalPage type="authName" />} />
          </Route>
        </Route>

        <Route element={<ConciergeUserProtectedRoute />}>
          <Route element={<ConciergeUserLayout />}>
            <Route path="/concierge/manage" element={<AdsModalPage type="conciergeUserMy" />} />
            <Route path="/concierge/manage/my" element={<AdsModalPage type="conciergeUserMy" />} />
            <Route path="/concierge/manage/history" element={<AdsModalPage type="conciergeUserHistory" />} />
            <Route path="/concierge/manage/payments" element={<AdsModalPage type="conciergeUserPayments" />} />
            <Route path="/concierge/manage/refunds" element={<AdsModalPage type="conciergeUserRefunds" />} />
            <Route path="/concierge/manage/change-pin" element={<AdsModalPage type="conciergeUserChangePin" />} />
          </Route>
        </Route>

        {/* SUPER 전용 보호 영역 */}
        <Route element={<ProtectedRoute roles={['SUPER']} />}>
          <Route element={<AdminLayout />}>
            <Route path="/admin/users" element={<AdsModalPage type="cmsUsers" />} />
            <Route path="/admin/users/:user_id" element={<AdsModalPage type="cmsUsersDetail" />} />
            <Route path="/admin/deleted/users" element={<AdsModalPage type="cmsDeletedUsers" />} />
            <Route path="/admin/list" element={<AdsModalPage type="adminList" />} />
            <Route path="/admin/list/create" element={<AdsModalPage type="adminCreate" />} />
            <Route path="/admin/:id" element={<AdsModalPage type="adminDetail" />} />




            <Route path="/concierge/:id" element={<AdsModalPage type="conciergeDetail" />} />
            <Route path="/concierge/system/list" element={<AdsModalPage type="conciergeSystemList" />} />
            <Route path="/test/period" element={<AdsModalPage type="testPeriod" />} />
          </Route>
        </Route>


        {/* 나머지 공개 라우트 */}
        <Route path="/ads/fee" element={<AdsModalPage type="fee" />} />
        <Route path="/ads/start" element={<AdsModalPage type="start" />} />
        <Route path="/ads/login/:storeBusinessNumber" element={<AdsModalPage type="login" />} />
        <Route path="/ads/login" element={<AdsModalPage type="appLogin" />} />
        <Route path="/ads/permission" element={<AdsModalPage type="permission" />} />
        <Route path="/ads/support" element={<AdsModalPage type="support" />} />

        <Route path="/ads/register" element={<AdsModalPage type="register" />} />
        <Route path="/ads/alarm" element={<AdsModalPage type="alarmList" />} />
        <Route path="/ads/notice" element={<AdsModalPage type="notice" />} />
        <Route path="/ads/notice/:notice_no" element={<AdsModalPage type="detail" />} />
        <Route path="/ads/notice/create" element={<AdsModalPage type="create" />} />
        <Route path="/ads/notice/edit/:notice_no" element={<AdsModalPage type="edit" />} />
        <Route path="/ads/faq" element={<AdsModalPage type="faq" />} />
        <Route path="/ads/faq/create" element={<AdsModalPage type="faqCreate" />} />
        <Route path="ads/policy" element={<AdsModalPage type="policy" />} />
        <Route path="/ads/main" element={<AdsModalPage type="main" />} />
        <Route path="/ads/reserve" element={<AdsModalPage type="reserve" />} />
        <Route path="/ads/reserve/create" element={<AdsModalPage type="reserveCreate" />} />
        <Route path="/ads/reserve/update" element={<AdsModalPage type="reserveUpdate" />} />
        <Route path="/ads/inquiry" element={<AdsModalPage type="inquiry" />} />
        <Route path="/ads/inquiry/history" element={<AdsModalPage type="inquiryHistory" />} />
        <Route path="/ads/callcenter" element={<AdsModalPage type="callcenter" />} />

        <Route path="/auto/marketing/prompt/loading" element={<AdsModalPage type="autoPrompt" />} />
        <Route path="/auto/marketing" element={<AdsModalPage type="auto" />} />
        <Route path="/auto/marketing/result" element={<AdsModalPage type="autoResult" />} />
        <Route path="/auto/marketing/regen/loading" element={<AdsModalPage type="autoRegenLoading" />} />
        <Route path="/auto/marketing/update" element={<AdsModalPage type="autoUpdate" />} />
        <Route path="/manual/marketing/ai" element={<AdsModalPage type="manualAI" />} />
        <Route path="/manual/marketing/ai/loading" element={<AdsModalPage type="manualAILoading" />} />
        <Route path="/manual/marketing/ai/result" element={<AdsModalPage type="manualAIResult" />} />
        <Route path="/manual/marketing/ai/regen" element={<AdsModalPage type="manualAIRegen" />} />
        <Route path="/manual/marketing/final" element={<AdsModalPage type="manualFinal" />} />
        <Route path="/manual/marketing/camera/loading" element={<AdsModalPage type="cameraLoading" />} />
        <Route path="/manual/marketing/gallery" element={<AdsModalPage type="gallery" />} />
        <Route path="/manual/marketing/gallery/stored-images" element={<AdsModalPage type="storedImagePicker" />} />
        <Route path="/manual/marketing/gallery/result" element={<AdsModalPage type="galleryResult" />} />
        <Route path="/manual/marketing/gallery/regen" element={<AdsModalPage type="galleryRegen" />} />

        <Route path="/event/marketing" element={<AdsModalPage type="eventMarketing" />} />
        <Route path="/event/marketing/ai" element={<AdsModalPage type="eventAI" />} />
        <Route path="/event/marketing/ai/loading" element={<AdsModalPage type="eventAILoading" />} />
        <Route path="/event/marketing/gallery" element={<AdsModalPage type="eventGallery" />} />
        <Route path="/event/marketing/camera" element={<AdsModalPage type="eventCamera" />} />

        <Route path="/mypage" element={<AdsModalPage type="mypage" />} />
        <Route path="/myrecord" element={<AdsModalPage type="myrecord" />} />
        <Route path="/myticket" element={<AdsModalPage type="myticket" />} />
        <Route path="/myticket/non/tab" element={<AdsModalPage type="myticketNonTab" />} />
        <Route path="/myticket/purchased" element={<AdsModalPage type="myticketPurchased" />} />
        <Route path="/usage/history" element={<AdsModalPage type="myUsageHistory" />} />
        <Route path="/myplan/cancel" element={<AdsModalPage type="myplanCancel" />} />
        <Route path="/myinfo" element={<AdsModalPage type="myinfo" />} />
        <Route path="/mycalendar" element={<AdsModalPage type="mycalendar" />} />
        <Route path="/mycalendar/detail" element={<AdsModalPage type="mycalendarDetail" />} />

        <Route path="/setting" element={<AdsModalPage type="setting" />} />
        <Route path="/cancel/user" element={<AdsModalPage type="cancelUser" />} />
        <Route path="/cancel/reason" element={<AdsModalPage type="cancelUserReason" />} />
        <Route path="/cancel/complete" element={<AdsModalPage type="cancelUserComplete" />} />
        
        <Route path="/testView" element={<AdsModalPage type="componentTestView" />} />
      </Routes>

      {/* 모달 라우트: background가 있을 때만 오버레이로 띄움 */}
      {background && (
        <Routes>
          <Route path="/inapp/address" element={<AdsModalPage type="inappAddress" />} />
        </Routes>
      )}
    </>
  );
}

export default function Router() {
  const RouterImpl = isWebView ? MemoryRouter : BrowserRouter;

  return (
    <UIStateProvider>
      <AuthProvider>
        <ConciergeUserAuthProvider>
          <RouterImpl {...(isWebView ? { initialEntries: [initialEntry] } : {})}>
            <RecoveryOverlay />
            <BackController rootPath="/ads/main" />
            <AppRoutes />
          </RouterImpl>
        </ConciergeUserAuthProvider>
      </AuthProvider>
    </UIStateProvider>
  );
}
